import { CuentaRelacionada, PersonaDetalle } from "../schema/person.schema";
import {
  Button,
  Checkbox,
  CheckboxCheckedChangeDetails,
  Flex,
  Table,
  Text,
  useDisclosure,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { v4 } from "uuid";
import { AppColors } from "@/styles/colors";
import { NoDataSmall } from "@/components/common/section-components/NoDataSmall";
import { lazy, Suspense, useState } from "react";
import { Link, Unlink } from "lucide-react";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import {
  unlinkAccounts,
  UnlinkAccountsProps,
} from "@/services/persons.service";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";

interface Props {
  personData: Partial<PersonaDetalle> | null;
  fetchPersonDetail: (id: number) => Promise<void>;
}

export const AssociatedAccounts = ({
  personData,
  fetchPersonDetail,
}: Props) => {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<number[]>([]);

  const [loadingUnlinkAccounts, setLoadingUnlinkAccounts] =
    useState<boolean>(false);

  const handleItemCheckChange = (
    changes: CheckboxCheckedChangeDetails,
    item: CuentaRelacionada,
  ) => {
    setSelection((prev) => {
      return changes.checked
        ? [...prev, item?.cuenta]
        : selection.filter((id) => {
            return id !== item?.cuenta;
          });
    });
  };

  const { fetchPermissions } = useRoles();

  const handleLinkAccounts = async () => {
    navigate(
      AppRoutes.PERSON_ASSOCIATE_ACCOUNT.replace(":id", String(personData?.id)),
    );
  };

  const handleUnlinkAccounts = async () => {
    setLoadingUnlinkAccounts(true);
    try {
      const objToSend: UnlinkAccountsProps = {
        idPersona: Number(personData?.id),
        cuentas: selection?.map(String),
      };

      await unlinkAccounts(objToSend);

      setSelection([])
      toaster.create({
        type: "success",
        title: "Cuentas seleccionadas desasociadas con éxito.",
      });

      await fetchPersonDetail(Number(personData?.id));

      setOpenUnlink(false);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
        AppRoutes.PERSON_DETAIL.replace(":id", String(personData?.id)),
        {
          tab: "cuentas_asociadas",
        },
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.response?.data?.message ??
            "No se pudo desasociar las cuentas seleccionadas.",
        });
      }
    } finally {
      setLoadingUnlinkAccounts(false);
      setOpenUnlink(false);
    }
  };

  const { open: openUnlink, setOpen: setOpenUnlink } = useDisclosure();

  const handleGoToAccount = (cuenta: string) => {
    navigate(`${AppRoutes.ACCOUNTS_DETAIL.replace(":id", cuenta)}`, {
      state: {
        from: AppRoutes.PERSON_DETAIL.replace(":id", String(personData?.id)),
      },
    });
  };

  const { userPermissions } = useRoles();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.PERSONS && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex flexDir="column">
      {hasWriteAccess && (
        <Flex justifyContent="end" padding="16px" gap="8px">
          <Button
            paddingX="8px"
            bgColor={AppColors.GREEN_PRIMARY}
            onClick={handleLinkAccounts}
          >
            <Link />
            <Text>Asociar cuenta</Text>
          </Button>
          <Button
            variant="solid"
            paddingX="8px"
            bgColor={AppColors.BLUE_TEXT}
            disabled={!selection?.length}
            onClick={() => setOpenUnlink(true)}
            data-testid="unlink-accept"
          >
            <Unlink />
            <Text>Desasociar</Text>
          </Button>
        </Flex>
      )}

      {personData?.cuentasRelacionadas?.length ? (
        <Table.ScrollArea
          borderRadius="12px"
          paddingTop="10px"
          css={{
            "@media (max-width: 1350px) and (min-width: 1272px)": {
              maxWidth: "900px",
              height: "240px",
            },
            "@media (max-width: 1414px) and (min-width: 1351px)": {
              maxWidth: "1100px",
              height: "310px",
            },
            "@media (max-width: 1590px) and (min-width: 1415px)": {
              maxWidth: "1250px",
              height: "390px",
            },
            "@media (max-width: 1696px) and (min-width: 1591px)": {
              maxWidth: "1350px",
              height: "440px",
            },
            "@media (max-width: 1900px)": {
              maxWidth: "1500px",
              height: "490px",
            },
            "@media (min-width: 1901px)": {
              height: "490px",
            },
          }}
        >
          <Table.Root
            size="sm"
            striped
            stickyHeader
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Table.Header>
              <Table.Row>
                {hasWriteAccess && (
                  <Table.ColumnHeader w="6">
                    <Text
                      padding="16px"
                      color={AppColors.GRAY_TEXT}
                      fontWeight={700}
                      textTransform="uppercase"
                      textAlign="center"
                      fontSize="12px"
                    >
                      SELECCIONAR
                    </Text>
                  </Table.ColumnHeader>
                )}

                <Table.ColumnHeader>
                  <Text
                    padding="16px"
                    color={AppColors.GRAY_TEXT}
                    fontWeight={700}
                    textTransform="uppercase"
                    fontSize="12px"
                  >
                    CUENTA
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  <Text
                    padding="16px"
                    color={AppColors.GRAY_TEXT}
                    fontWeight={700}
                    textTransform="uppercase"
                    fontSize="12px"
                  >
                    Razón social
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  <Text
                    padding="16px"
                    color={AppColors.GRAY_TEXT}
                    fontWeight={700}
                    textTransform="uppercase"
                    fontSize="12px"
                  >
                    CUIT
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  <Text
                    padding="16px"
                    color={AppColors.GRAY_TEXT}
                    fontWeight={700}
                    textTransform="uppercase"
                    fontSize="12px"
                  >
                    Zona comercial
                  </Text>
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {personData?.cuentasRelacionadas?.map((item, index) => (
                <Table.Row key={v4()}>
                  {hasWriteAccess && (
                    <Table.Cell paddingX={"35px"} width={"100px"}>
                      <Checkbox.Root
                        top="1"
                        paddingLeft={3}
                        aria-label="Select row"
                        checked={selection.includes(item?.cuenta)}
                        onCheckedChange={(changes) =>
                          handleItemCheckChange(changes, item)
                        }
                      >
                        <Checkbox.HiddenInput data-testid="row-selection-checkbox" />
                        <Checkbox.Control
                          borderColor={AppColors.GRAY_BORDER_1}
                          cursor="pointer"
                        />
                      </Checkbox.Root>
                    </Table.Cell>
                  )}

                  <Table.Cell>
                    <ChakraLink
                      data-testid={`associated-accounts-link-${index}`}
                      onClick={() => handleGoToAccount(String(item?.cuenta))}
                    >
                      <Text
                        padding="16px"
                        fontWeight={400}
                        fontSize="12px"
                        color={AppColors.BLACK_TEXT_SECONDARY}
                      >
                        {item?.cuenta ?? "-"}
                      </Text>
                    </ChakraLink>
                  </Table.Cell>
                  <Table.Cell>
                    <Text
                      padding="16px"
                      fontWeight={400}
                      fontSize="12px"
                      color={AppColors.BLACK_TEXT_SECONDARY}
                    >
                      {item?.razonSocial ?? "-"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text
                      padding="16px"
                      fontWeight={400}
                      fontSize="12px"
                      color={AppColors.BLACK_TEXT_SECONDARY}
                    >
                      {item?.cuit ?? "-"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text
                      padding="16px"
                      fontWeight={400}
                      fontSize="12px"
                      color={AppColors.BLACK_TEXT_SECONDARY}
                    >
                      {item?.zonaComercial ?? "-"}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Table.ScrollArea>
      ) : (
        <NoDataSmall />
      )}
      <Suspense>
        <UnlinkAccountModalDynamic
          openUnlinkAccount={openUnlink}
          setOpenUnlinkAccount={setOpenUnlink}
          accountsToUnlink={selection?.length}
          handleAccept={handleUnlinkAccounts}
          loadingAccept={loadingUnlinkAccounts}
        />
      </Suspense>
    </Flex>
  );
};

const UnlinkAccountModalDynamic = lazy(() =>
  import("@/components/modal/UnlinkAccountModal").then((el) => ({
    default: el["UnlinkAccountModal"],
  })),
);
